import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { fetchNegativeLeads, fetchNegativeLeadsStats, createNegativeLead, transferNegativeToPipeline } from '@/lib/api';
import { formatINR } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, ArrowRightLeft, Search, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import BrandWatermark from '@/components/BrandWatermark';

const REASONS = ["No Budget", "No Timing", "Wrong Fit", "No Response", "Competitor Won", "Duplicate", "Other"];
const REASON_COLOR = {
    "No Budget": "text-[#FF9F0A]",
    "No Timing": "text-[#189290]",
    "Wrong Fit": "text-slate-500",
    "No Response": "text-slate-500",
    "Competitor Won": "text-[#FF453A]",
    "Duplicate": "text-purple-400",
    "Other": "text-slate-500",
};

const emptyForm = { customer_name: '', contact_person: '', email: '', mobile: '', service_line: '', proposal_value: 0, sales_owner: '', reason: 'No Budget', source: '', area: '', notes: '' };

export default function NegativeLeads() {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [q, setQ] = useState('');
    const [reasonFilter, setReasonFilter] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [transferring, setTransferring] = useState(null);
    const nav = useNavigate();

    const load = async () => {
        const params = {};
        if (q) params.q = q;
        if (reasonFilter) params.reason = reasonFilter;
        const [list, s] = await Promise.all([fetchNegativeLeads(params), fetchNegativeLeadsStats()]);
        setItems(list); setStats(s);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [reasonFilter]);
    useEffect(() => {
        const t = setTimeout(load, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q]);

    const submitNew = async () => {
        if (!form.customer_name.trim()) { toast.error('Customer name required'); return; }
        try {
            await createNegativeLead({ ...form, proposal_value: Number(form.proposal_value) });
            toast.success('Negative lead added');
            setOpenDialog(false); setForm(emptyForm); load();
        } catch { toast.error('Create failed'); }
    };

    const transfer = async (item) => {
        if (!window.confirm(`Transfer "${item.customer_name}" back to the active pipeline? It will start at "Lead Identified".`)) return;
        setTransferring(item.id);
        try {
            const r = await transferNegativeToPipeline(item.id);
            toast.success(`Transferred to Pipeline as ${r.new_lead_id}`);
            load();
        } catch { toast.error('Transfer failed'); }
        setTransferring(null);
    };

    return (
        <div>
            <PageHeader
                title="Negative Leads"
                subtitle={`Disqualified pool · ${items.length} records`}
                actions={
                    <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                        <DialogTrigger asChild>
                            <Button data-testid="btn-new-negative" className="bg-[#016a6d] hover:bg-[#014e50] gap-2">
                                <Plus className="w-4 h-4" /> New Negative Lead
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-2xl max-h-[85vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">Add Negative Lead</DialogTitle>
                                <DialogDescription className="text-slate-500">These leads live in a separate pool and don't affect your pipeline metrics.</DialogDescription>
                            </DialogHeader>
                            <div className="grid grid-cols-2 gap-3">
                                <F label="Customer *" value={form.customer_name} onChange={v => setForm({ ...form, customer_name: v })} testId="neg-customer" />
                                <F label="Contact Person" value={form.contact_person} onChange={v => setForm({ ...form, contact_person: v })} testId="neg-contact" />
                                <F label="Email" value={form.email} onChange={v => setForm({ ...form, email: v })} testId="neg-email" />
                                <F label="Mobile" value={form.mobile} onChange={v => setForm({ ...form, mobile: v })} testId="neg-mobile" />
                                <F label="Service Line" value={form.service_line} onChange={v => setForm({ ...form, service_line: v })} testId="neg-service" />
                                <F label="Sales Owner" value={form.sales_owner} onChange={v => setForm({ ...form, sales_owner: v })} testId="neg-owner" />
                                <F label="Area" value={form.area} onChange={v => setForm({ ...form, area: v })} testId="neg-area" />
                                <F label="Source" value={form.source} onChange={v => setForm({ ...form, source: v })} testId="neg-source" />
                                <F label="Proposal Value (₹)" type="number" value={form.proposal_value} onChange={v => setForm({ ...form, proposal_value: v })} testId="neg-value" />
                                <S label="Reason *" value={form.reason} onChange={v => setForm({ ...form, reason: v })} options={REASONS} testId="neg-reason" />
                            </div>
                            <div>
                                <label className="overline block mb-1">Notes</label>
                                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="neg-notes" className="w-full bg-[#F1F5F9] border border-[#E2E8F0] text-slate-900 rounded-md p-2 text-sm" />
                            </div>
                            <DialogFooter>
                                <Button onClick={submitNew} data-testid="btn-save-negative" className="bg-[#016a6d] hover:bg-[#014e50]">Add Negative Lead</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            <div className="p-6 space-y-4">
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="surface-1 rounded-md p-5" data-testid="neg-kpi-total">
                            <div className="overline">Total Negative</div>
                            <div className="font-display text-3xl font-bold text-slate-900 mt-2 tabular-nums">{stats.total}</div>
                        </div>
                        <div className="surface-1 rounded-md p-5" data-testid="neg-kpi-value">
                            <div className="overline">Lost Potential</div>
                            <div className="font-display text-3xl font-bold text-[#FF9F0A] mt-2 tabular-nums">{formatINR(stats.total_value)}</div>
                        </div>
                        <div className="surface-1 rounded-md p-5 md:col-span-2" data-testid="neg-kpi-reasons">
                            <div className="overline">Top Reasons</div>
                            <div className="flex flex-wrap gap-2 mt-2">
                                {stats.by_reason.slice(0, 5).map(r => (
                                    <div key={r.reason} className="text-xs px-2 py-1 rounded bg-[#F1F5F9] border border-[#E2E8F0]">
                                        <span className={REASON_COLOR[r.reason] || 'text-slate-500'}>{r.reason}</span>
                                        <span className="ml-2 text-slate-900 tabular-nums">{r.count}</span>
                                    </div>
                                ))}
                                {stats.by_reason.length === 0 && <span className="text-xs text-slate-500">No data yet</span>}
                            </div>
                        </div>
                    </div>
                )}

                <div className="surface-1 rounded-md p-4 flex flex-wrap gap-3 items-center">
                    <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                        <Search className="w-4 h-4 text-slate-500" />
                        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search customer, contact..." data-testid="neg-search" className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9" />
                    </div>
                    <select value={reasonFilter} onChange={e => setReasonFilter(e.target.value)} data-testid="neg-filter-reason" className="h-9 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md text-slate-900 text-sm px-3">
                        <option value="">Reason: All</option>
                        {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>

                <div className="surface-1 rounded-md overflow-hidden" data-testid="negative-leads-table">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#F1F5F9] border-b border-[#E2E8F0]">
                                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    <th className="px-4 py-3">Lead ID</th>
                                    <th className="px-4 py-3">Customer</th>
                                    <th className="px-4 py-3">Reason</th>
                                    <th className="px-4 py-3">Owner</th>
                                    <th className="px-4 py-3">Service</th>
                                    <th className="px-4 py-3 text-right">Value</th>
                                    <th className="px-4 py-3 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(l => (
                                    <tr key={l.id} data-testid={`neg-row-${l.lead_id}`} className="border-b border-[#E2E8F0] hover:bg-[#FFFFFF] transition-colors">
                                        <td className="px-4 py-3 text-slate-500 tabular-nums text-xs cursor-pointer" onClick={() => nav(`/negative-leads/${l.id}`)}>{l.lead_id}</td>
                                        <td className="px-4 py-3 text-slate-900 cursor-pointer" onClick={() => nav(`/negative-leads/${l.id}`)}>{l.customer_name}<div className="text-[10px] text-slate-500">{l.contact_person}</div></td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs font-medium ${REASON_COLOR[l.reason] || 'text-slate-500'}`}>{l.reason}</span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-700 text-xs">{l.sales_owner || '—'}</td>
                                        <td className="px-4 py-3 text-slate-700 text-xs">{l.service_offered || l.service_line || '—'}</td>
                                        <td className="px-4 py-3 text-right text-slate-900 tabular-nums">{formatINR(l.proposal_value)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <Button
                                                size="sm"
                                                onClick={() => transfer(l)}
                                                disabled={transferring === l.id}
                                                data-testid={`btn-transfer-${l.lead_id}`}
                                                className="h-7 bg-[#016a6d] hover:bg-[#014e50] text-xs gap-1"
                                            >
                                                <ArrowRightLeft className="w-3 h-3" />
                                                {transferring === l.id ? 'Moving…' : 'To Pipeline'}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {items.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="text-center py-16 relative">
                                            <BrandWatermark size={220} opacity={0.15} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                                            <div className="relative">
                                                <XCircle className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                                                <div className="text-sm text-slate-500">No negative leads yet</div>
                                                <div className="text-xs text-slate-400 mt-1">Disqualify a lead from the pipeline to see it here</div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function F({ label, value, onChange, type = 'text', testId }) {
    return (
        <div>
            <label className="overline block mb-1">{label}</label>
            <Input value={value} type={type} onChange={e => onChange(e.target.value)} data-testid={testId} className="bg-[#F1F5F9] border-[#E2E8F0] text-slate-900 h-9" />
        </div>
    );
}
function S({ label, value, onChange, options, testId }) {
    return (
        <div>
            <label className="overline block mb-1">{label}</label>
            <select value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testId} className="w-full h-9 bg-[#F1F5F9] border border-[#E2E8F0] rounded-md text-slate-900 text-sm px-3">
                {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}
